import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Form, Grid, Input, Button, Typography, Message } from "@arco-design/web-react";

import { endpoints } from "../api/endpoints";
import { Logo } from "../components/Logo";
import { LanguageSelect } from "../components/LanguageSelect/LanguageSelect";
import { routes } from "../routes/routes";
import { clearAuthSession } from "../utils/clearAuthSession";
import { getBoxBackgroundColor, getButtonColor, getButtonTextColor, getColorAccent1, getTextColor } from "../utils/cssVariables";

import { getAuthLogoType } from "../theme";

import "./ConfirmResetPassword.css";

const Row = Grid.Row;
const FormItem = Form.Item;

export const ConfirmResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isAdminInvite = params.get("invite") === "admin";
  const [sessionCleared, setSessionCleared] = useState(false);

  useEffect(() => {
    let cancelled = false;
    clearAuthSession().then(() => {
      if (!cancelled) {
        setSessionCleared(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async () => {
    if (form) {
      try {
        const values = await form.validate();
        const password = values.password;

        const params = new URLSearchParams(location.search);
        const email = params.get("email");

        if (!email) {
          Message.error(t("notification_messages.invalid_reset_link"));
          return;
        }

        await axios.post(endpoints.resetPassword, {
          email: email,
          password: password,
        });

        Message.success(t("notification_messages.reset_password_success"));
        navigate(routes.LOGIN);
      } catch (error) {
        console.error("Error resetting password:", error);
        Message.error(t("notification_messages.reset_password_error"));
      }
    }
  };

  if (!sessionCleared) {
    return null;
  }

  return (
    <div
      className="confirm-reset-password"
      style={{ backgroundColor: getBoxBackgroundColor() }}
    >
      <Logo variant="auth" style={{ marginTop: 10 }} type={getAuthLogoType()} />
      <div className="confirm-reset-password__lang">
        <LanguageSelect color={getTextColor()} />
      </div>
      <Form onSubmit={onSubmit} form={form}>
        <Row justify="center">
          <div className="confirm-reset-password__form-body">
            <div>
              <Typography.Text
                className="confirm-reset-password__title alice-regular"
                style={{ color: getColorAccent1() }}
                type="success"
              >
                {isAdminInvite
                  ? t("reset_password_page.admin_setup_title")
                  : t("reset_password_page.reset_password")}
              </Typography.Text>
              {isAdminInvite && (
                <Typography.Paragraph
                  className="confirm-reset-password__subtitle"
                  style={{ color: getTextColor(), marginTop: 8 }}
                >
                  {t("reset_password_page.admin_setup_description")}
                </Typography.Paragraph>
              )}
            </div>
            <div className="confirm-reset-password__fields">
              <FormItem
                layout="vertical"
                label={t("reset_password_page.new_password_label")}
                field="password"
                rules={[
                  {
                    validator: (v, cb) => {
                      return !v
                        ? cb(t("reset_password_page.password_required"))
                        : v.length < 6
                          ? cb(t("reset_password_page.short_password"))
                          : cb();
                    },
                  },
                ]}
                validateTrigger="onChange"
              >
                <Input.Password
                  placeholder={t(
                    "reset_password_page.new_password_placeholder"
                  )}
                  autoComplete={"off"}
                />
              </FormItem>
              <FormItem
                layout="vertical"
                label={t("reset_password_page.confirm_password_label")}
                field="passwordConfirmation"
                rules={[
                  {
                    validator: (v, cb) => {
                      return v !== form.getFieldValue("password")
                        ? cb(t("reset_password_page.passwords_do_not_match"))
                        : cb();
                    },
                  },
                ]}
                validateTrigger="onChange"
              >
                <Input.Password
                  placeholder={t(
                    "reset_password_page.confirm_password_placeholder"
                  )}
                  autoComplete={"off"}
                />
              </FormItem>
              <Button
                htmlType="submit"
                className="confirm-reset-password__submit"
                style={{
                  color: getButtonTextColor(),
                  backgroundColor: getButtonColor(),
                }}
                size={"large"}
                shape={"round"}
              >
                {isAdminInvite
                  ? t("reset_password_page.admin_setup_submit")
                  : t("reset_password_page.reset_password")}
              </Button>
            </div>
          </div>
        </Row>
      </Form>

      <div className="confirm-reset-password__footer">
        <Typography.Paragraph>
          {t("reset_password_page.remember_credentials")}
          <NavLink to={routes.LOGIN}>
            <Button style={{ padding: "0 0 0 4px" }} type="text" status="success">
              <span style={{ color: getColorAccent1() }}>
                {t("reset_password_page.back_to_login")}
              </span>
            </Button>
          </NavLink>
        </Typography.Paragraph>
      </div>
    </div>
  );
};
